// src/context/AuthContext.jsx
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { hasPermission as checkPermission, hasAnyPermission as checkAnyPermission } from '../utils/permissionHelper';
import {
    login,
    register,
    logout,
    getProfile,
    verify2FA,
    get2FAStatus,
    enable2FA,
    confirm2FA,
    disable2FA,
    regenerateRecoveryCodes
} from '../api/authApi';

const AuthContext = createContext();

const initialState = {
    user: null,
    token: localStorage.getItem('token'),
    loading: true,
    error: null
};

const authReducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                loading: false,
                error: null
            };
        case 'LOGIN_FAILURE':
            return {
                ...state,
                user: null,
                token: null,
                loading: false,
                error: action.payload
            };
        case 'LOGOUT':
            return {
                ...state,
                user: null,
                token: null,
                loading: false
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload
            };
        case 'REGISTER_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                loading: false,
                error: null
            };
        case 'REGISTER_FAILURE':
            return {
                ...state,
                user: null,
                token: null,
                loading: false,
                error: action.payload
            };
        default:
            return state;
    }
};

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            getProfile()
                .then((response) => {
                    dispatch({
                        type: 'LOGIN_SUCCESS',
                        payload: {
                            user: response.data,
                            token
                        }
                    });
                })
                .catch((error) => {
                    console.error('Profile check failed:', error);
                    // Only logout if it's strictly a 401 (Unauthenticated) which usually means token is expired/invalid
                    // The axios interceptor already handles 401 redirects, so we mostly filter out 500s here.
                    if (error.response && error.response.status === 401) {
                        localStorage.removeItem('token');
                        dispatch({ type: 'LOGOUT' });
                    } else {
                        // For other errors (500, network), we keep the user 'technically' logged in
                        // but maybe set an error state?
                        // For now, let's allow them to stay to see the error message on screen if possible.
                        dispatch({ type: 'SET_LOADING', payload: false });
                    }
                })
                .finally(() => {
                    dispatch({ type: 'SET_LOADING', payload: false });
                });
        } else {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    const handleLogin = async (credentials) => {
        try {
            const response = await login(credentials);

            // Handle 2FA Required
            if (response.data.two_factor_required) {
                return {
                    success: true,
                    twoFactorRequired: true,
                    userId: response.data.user_id
                };
            }

            const { user, token } = response.data;
            localStorage.setItem('token', token);
            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                    user,
                    token
                }
            });
            return { success: true, user };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'فشل تسجيل الدخول';
            dispatch({
                type: 'LOGIN_FAILURE',
                payload: errorMessage
            });
            return { success: false, error: errorMessage };
        }
    };

    const handleVerify2FA = async (userId, code) => {
        try {
            const response = await verify2FA({ user_id: userId, code });
            const { user, token } = response.data.data;
            localStorage.setItem('token', token);
            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                    user,
                    token
                }
            });
            return { success: true, user };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'رمز التحقق غير صحيح';
            return { success: false, error: errorMessage };
        }
    };

    const handleRegister = async (userData) => {
        try {
            const response = await register(userData);

            // Handle Pending Approval Case
            if (response.data.status === 'pending_approval') {
                return {
                    success: true,
                    status: 'pending_approval',
                    message: response.data.message
                };
            }

            const { user, token } = response.data;
            localStorage.setItem('token', token);
            dispatch({
                type: 'REGISTER_SUCCESS',
                payload: {
                    user,
                    token
                }
            });
            return { success: true, user };
        } catch (error) {
            let errorMessage = 'فشل التسجيل';

            if (error.response?.data?.errors) {
                // تحويل أخطاء التحقق إلى رسالة واحدة
                const errors = error.response.data.errors;
                errorMessage = Object.values(errors).flat().join(', ');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            dispatch({
                type: 'REGISTER_FAILURE',
                payload: errorMessage
            });
            return {
                success: false,
                error: errorMessage
            };
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('token');
            dispatch({ type: 'LOGOUT' });
        }
    };

    /* New Method to refresh User Profile (for feature toggle updates) */
    const refreshProfile = async () => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const response = await getProfile();
                dispatch({
                    type: 'LOGIN_SUCCESS',
                    payload: {
                        user: response.data,
                        token
                    }
                });
                return response.data;
            } catch (error) {
                console.error('Failed to refresh profile:', error);
            }
        }
    };

    const handleGet2FAStatus = async () => {
        try {
            const response = await get2FAStatus();
            return response.data;
        } catch (error) {
            console.error('Failed to fetch 2FA status:', error);
            throw error;
        }
    };

    const handleEnable2FA = async () => {
        try {
            const response = await enable2FA();
            return response.data;
        } catch (error) {
            console.error('Failed to enable 2FA:', error);
            throw error;
        }
    };

    const handleConfirm2FA = async (code) => {
        try {
            const response = await confirm2FA(code);
            await refreshProfile();
            return response.data;
        } catch (error) {
            console.error('Failed to confirm 2FA:', error);
            throw error;
        }
    };

    const handleDisable2FA = async (code) => {
        try {
            const response = await disable2FA(code);
            await refreshProfile();
            return response.data;
        } catch (error) {
            console.error('Failed to disable 2FA:', error);
            throw error;
        }
    };

    const handleRegenerateRecoveryCodes = async () => {
        try {
            const response = await regenerateRecoveryCodes();
            return response.data;
        } catch (error) {
            console.error('Failed to regenerate recovery codes:', error);
            throw error;
        }
    };



    // Wrapper functions that bind the current user
    const hasPermission = (permission) => checkPermission(state.user, permission);
    const hasAnyPermission = (permissions) => checkAnyPermission(state.user, permissions);


    return (
        <AuthContext.Provider
            value={{
                ...state,
                login: handleLogin,
                register: handleRegister,
                logout: handleLogout,
                refreshProfile,
                verify2FA: handleVerify2FA,
                get2FAStatus: handleGet2FAStatus,
                enable2FA: handleEnable2FA,
                confirm2FA: handleConfirm2FA,
                disable2FA: handleDisable2FA,
                regenerateRecoveryCodes: handleRegenerateRecoveryCodes,
                hasPermission,
                hasAnyPermission
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
